import React from 'react';
import StackedList from "components/StackedList";
import StackedListItem from "components/StackedListItem";
import {DeviceMobileIcon, QrcodeIcon} from "@heroicons/react/outline";
import Link from "components/Link";
import ngage from "services/ngage";
import Card from "components/Card";
import googlePlayBadge from '../../assets/google-play-badge.png';
import appStoreBadge from '../../assets/app-store-badge.png';
import huaweiAppGalleryBadge from '../../assets/huawei-appgallery-badge.png';
import PlayStoreMarkup from "components/AppCard/PlayStoreMarkup";
import AppStoreMarkup from "components/AppCard/AppStoreMarkup";
import {Operator} from "types/Operator";
import AppPlatform from "types/AppPlatform";
import {AppInfo} from "types/AppInfo";
import Button from "components/Button";
import {isAndroid, isBrowser, isIOS} from "mobile-device-detect";
import AppGalleryMarkup from "components/AppCard/AppGalleryMarkup";

type Props = {
    operator: Operator
    app: AppInfo
}

const headings: Record<AppPlatform, string> = {
    [AppPlatform.ANDROID]: 'Android Devices',
    [AppPlatform.HUAWEI]: 'Huawei Devices',
    [AppPlatform.IOS]: 'iOS Devices',
    [AppPlatform.WEB]: 'Web Browsers',
}

const badges: Record<AppPlatform, string> = {
    [AppPlatform.ANDROID]: googlePlayBadge,
    [AppPlatform.HUAWEI]: huaweiAppGalleryBadge,
    [AppPlatform.IOS]: appStoreBadge,
    [AppPlatform.WEB]: '',
}

const isPlatform: Record<AppPlatform, boolean> = {
    [AppPlatform.ANDROID]: isAndroid,
    [AppPlatform.HUAWEI]: false, //TODO: Find a way to detect a Huawei device
    [AppPlatform.IOS]: isIOS,
    [AppPlatform.WEB]: isBrowser,
}

function AppCard({operator, app}: Props) {
    const action = isPlatform[app.platform] && (
        <Button icon={DeviceMobileIcon} onClick={() => window.location.href = ngage.apps.openUrl()}>
            Open
        </Button>
    )

    return (
        <Card heading={app.name} subheading={headings[app.platform]} action={action}>

            <div className="flex items-center justify-center">
                <div className="bg-gray-200 rounded-lg p-5 shadow-sm">
                    <img src={app.qr_code} alt={`Scan to download ${app.name}`} className="max-h-64 rounded-md"/>
                </div>
            </div>

            {/* QR code downloads */}
            <div className="mt-6">
                <span className="block text-sm font-medium text-gray-700">
                    QR Codes
                </span>
                <div className="mt-1">
                    <StackedList>
                        <StackedListItem
                            icon={QrcodeIcon}
                            action={<Link to={ngage.apps.downloadQrCodeUrl(app.id, "png", operator.domain)} download>Download</Link>}>
                            PNG file (1000px square)
                        </StackedListItem>
                        <StackedListItem
                            icon={QrcodeIcon}
                            action={<Link to={ngage.apps.downloadQrCodeUrl(app.id, "svg", operator.domain)} download>Download</Link>}>
                            SVG file
                        </StackedListItem>
                        <StackedListItem
                            icon={QrcodeIcon}
                            action={<Link to={ngage.apps.downloadQrCodeUrl(app.id, "eps", operator.domain)} download>Download</Link>}>
                            EPS file
                        </StackedListItem>
                    </StackedList>
                </div>
            </div>

            {!!app.url && app.platform !== 'web' && <>

                {/* Store badge / link */}
                <div className="mt-10 flex items-center justify-center">
                    <a href={app.url} target="_blank" rel="noreferrer nofollow">
                        <img
                            className="max-h-14"
                            src={badges[app.platform]}
                            alt={headings[app.platform] + ' badge'}
                        />
                    </a>
                </div>

                {/* HTML snippet */}
                <div className="mt-6">
                    <span className="block text-sm font-medium text-gray-700">
                        Badge
                    </span>
                    <div className="mt-1">
                        <pre className="bg-gray-100 text-gray-700 flex overflow-auto scrollbar-thin scrollbar- scrollbar-track-gray-200 scrollbar-thumb-gray-400 rounded-b-lg text-xs leading-[1.5714285714] sm:rounded-t-lg language-html">
                            <code className="p-4">
                                {app.platform === AppPlatform.ANDROID && <PlayStoreMarkup url={app.url} />}

                                {app.platform === AppPlatform.HUAWEI && <AppGalleryMarkup url={app.url} />}

                                {app.platform === AppPlatform.IOS && <AppStoreMarkup url={app.url} />}
                            </code>
                        </pre>
                    </div>
                </div>
            </>}
        </Card>
    );
}

export default AppCard;